import { ArrowForward } from '@mui/icons-material';
import { Box, Divider, LinearProgress, Typography } from '@mui/material';
import { SurgicalCaseEntity } from '@peritasai/repository';
import { Button } from '@peritasai/ui-components';
import { useTranslation } from 'react-i18next';
import { getCartActionLabel } from '../utils/cartAction';
import { getCountdown } from '../utils/countdown';
import { CASE_CART_QUEUE_STAGE, CaseCartAssemblyQueueStage } from '../utils/queues';
import { CartStatusPill } from './CartStatusPill';
import { ReactComponent as CartIcon } from './icons/cart.svg';

const cardSx = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '4px',
  height: '100%',
  borderRadius: '8px',
  background: '#fff',
  boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.12)',
  overflow: 'hidden',
};

const timeSectionSx = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  padding: '16px 16px 0',
};

const timeContentSx = {
  display: 'flex',
  flexDirection: 'column' as const,
  flex: '1 0 0',
  // Figma reserves this same 51px height whether the block shows the
  // 2-line "stage within" label + countdown, or the 1-line "Dispatched"/
  // "Complete" text — otherwise the divider and everything below it would
  // shift position depending on the card's queueStage.
  minHeight: '51px',
  justifyContent: 'center',
};

const stageLabelSx = {
  color: '#64748b',
  fontSize: '10px',
  fontWeight: 600,
  lineHeight: 'normal',
  letterSpacing: 'normal',
  textTransform: 'uppercase' as const,
};

const timeTextSx = {
  color: '#000',
  fontSize: '32px',
  fontWeight: 700,
  lineHeight: 'normal',
  letterSpacing: '-2.24px',
};

const dividerSx = { borderColor: 'rgba(100, 116, 139, 0.1)' };

const caseSectionSx = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '8px',
  padding: '4px 16px',
};

const roomRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
};

const roomBadgeSx = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 4px',
  borderRadius: '4px',
  backgroundColor: '#f4f4f4',
  fontSize: '12px',
  fontWeight: 500,
  color: '#000',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.4px',
  flexShrink: 0,
  maxWidth: '160px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

const caseStartSx = {
  color: '#64748b',
  fontSize: '12px',
  fontWeight: 500,
};

const truncateSx = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

const surgeonNameSx = {
  ...truncateSx,
  color: '#000',
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: 'normal',
  letterSpacing: 'normal',
};

const procedureSx = {
  ...truncateSx,
  color: '#000',
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: 'normal',
  letterSpacing: 'normal',
};

const progressSectionSx = {
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'flex-end',
  gap: '8px',
  padding: '4px 16px',
};

const progressLabelSx = {
  color: '#64748b',
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: 1.57,
  letterSpacing: '0.1px',
  whiteSpace: 'nowrap' as const,
};

const progressBarBaseSx = {
  width: '100%',
  height: '4px',
  borderRadius: '4px',
  backgroundColor: 'rgba(207, 216, 220, 0.6)',
  '& .MuiLinearProgress-bar': { borderRadius: '3px' },
};

const notStartedBarSx = {
  ...progressBarBaseSx,
  '& .MuiLinearProgress-bar': { display: 'none' },
};

const inProgressBarSx = {
  ...progressBarBaseSx,
  '& .MuiLinearProgress-bar': { ...progressBarBaseSx['& .MuiLinearProgress-bar'], backgroundColor: '#901de4' },
};

const stagedBarSx = {
  ...progressBarBaseSx,
  '& .MuiLinearProgress-bar': { ...progressBarBaseSx['& .MuiLinearProgress-bar'], backgroundColor: '#43a047' },
};

const cartFooterSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px',
  borderTop: '1px solid #eff1f3',
};

const cartInfoSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  minWidth: 0,
};

const cartIconContainerSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  backgroundColor: '#f4f4f4',
  flexShrink: 0,
};

const cartIconSx = {
  width: '20px',
  height: '14px',
  color: 'rgba(0, 0, 0, 0.54)',
  transform: 'scaleX(-1)',
};

const cartLabelSx = {
  color: '#64748b',
  fontSize: '12px',
  fontWeight: 500,
  letterSpacing: '0.4px',
  textTransform: 'uppercase' as const,
  lineHeight: 'normal',
};

const actionButtonSx = {
  height: '32px',
  padding: '8px 16px',
  fontSize: '11.6px',
  fontWeight: 700,
  textTransform: 'none' as const,
  borderRadius: '4px',
  '&&&': {
    paddingTop: '8px',
    paddingBottom: '8px',
  },
  '& .MuiButton-endIcon': {
    marginLeft: '4px !important',
    '& > svg': { fontSize: '16px !important' },
  },
};

const cartActionsSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexShrink: 0,
};

// Widths per Figma review (PVS-2865 rework comment): View = 61px, Send to OR = 106px.
const viewButtonSx = {
  ...actionButtonSx,
  minWidth: 0,
  maxWidth: '61px',
};

const sendToOrButtonSx = {
  ...actionButtonSx,
  // Shrink to fit short labels (e.g. the generic "Send to OR") instead of
  // always sitting at the 106px ceiling — only a longer location name grows
  // the button up to that cap, at which point the label itself truncates.
  width: 'fit-content',
  minWidth: 0,
  maxWidth: '106px',
};

const sendToOrLabelSx = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
  minWidth: 0,
};

const cartNameSx = {
  color: '#000',
  fontSize: '16px',
  fontWeight: 700,
  letterSpacing: '0.15px',
  lineHeight: 'normal',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

type CaseCartAssemblyRowProps = {
  caseItem: SurgicalCaseEntity;
  now: Date;
  queueStage: CaseCartAssemblyQueueStage;
  onSelect: (caseId: string) => void;
  onSendToOr?: (caseId: string) => void;
  isSendingToOr?: boolean;
};

export const CaseCartAssemblyRow = ({
  caseItem,
  now,
  queueStage,
  onSelect,
  onSendToOr,
  isSendingToOr = false,
}: CaseCartAssemblyRowProps) => {
  const { t } = useTranslation(['periverse']);
  const { hours, minutes, isAtRisk } = getCountdown(caseItem.dateTime, now);
  const isBuilding = queueStage === CASE_CART_QUEUE_STAGE.READY_TO_BUILD;
  const isArrived = queueStage === CASE_CART_QUEUE_STAGE.ARRIVED_IN_OR;
  const isNotStarted = isBuilding && caseItem.traysLoaded === 0 && caseItem.traysReserved === 0;
  const rawActionLabel = getCartActionLabel(caseItem.traysLoaded, caseItem.traysReserved, caseItem.traysRequired);
  const actionI18nKey =
    rawActionLabel === 'build'
      ? 'pages.caseCartAssembly.startLabel'
      : 'pages.caseCartAssembly.continueLabel';

  const cartLabel = caseItem.selectedCart
    ? [caseItem.cartReferenceNumber, caseItem.cartLocation].filter(Boolean).join(' • ')
    : t('pages.caseCartAssembly.noCartAssigned');
  const canSendToOr = Boolean(caseItem.selectedCart);
  const sendToOrLabel = canSendToOr
    ? t('pages.caseCartAssembly.sendToLocationLabel', {
        location: caseItem.cartLocation,
      })
    : t('pages.caseCartAssembly.sendToOrLabel');

  const traysLoadedOrReserved = caseItem.traysLoaded + caseItem.traysReserved;
  const progressValue = isNotStarted
    ? 0
    : caseItem.traysRequired > 0
      ? Math.min(100, Math.round((traysLoadedOrReserved / caseItem.traysRequired) * 100))
      : caseItem.getCartFillPercentage();
  const progressBarSx = isNotStarted ? notStartedBarSx : isBuilding ? inProgressBarSx : stagedBarSx;

  return (
    <Box sx={cardSx} data-testid="case-cart-assembly-row">
      {/* Section 1: Stage label + countdown/status text + status pill */}
      <Box sx={timeSectionSx}>
        <Box sx={timeContentSx}>
          {(queueStage === CASE_CART_QUEUE_STAGE.READY_TO_BUILD ||
            queueStage === CASE_CART_QUEUE_STAGE.READY_FOR_DISPATCH) && (
            <Typography sx={stageLabelSx}>
              {t('pages.caseCartAssembly.stageWithinLabel')}
            </Typography>
          )}
          <Typography sx={timeTextSx}>
            {isArrived
              ? t('pages.caseCartAssembly.completedLabel')
              : queueStage === CASE_CART_QUEUE_STAGE.DISPATCHED
                ? t('pages.caseCartAssembly.dispatchedStatusLabel')
                : t('pages.caseCartAssembly.countdownLabel', { hours, minutes })}
          </Typography>
        </Box>
        {!isArrived && <CartStatusPill isAtRisk={isAtRisk} />}
      </Box>

      <Divider sx={dividerSx} />

      {/* Section 2: OR badge + case start time, surgeon, procedure */}
      <Box sx={caseSectionSx}>
        <Box sx={roomRowSx}>
          <Box sx={roomBadgeSx} title={caseItem.room}>
            {caseItem.room}
          </Box>
          <Typography sx={caseStartSx}>
            {caseItem.getFormattedTime()}{' '}
            {t('pages.caseCartAssembly.caseStartSuffix')}
          </Typography>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={surgeonNameSx} title={caseItem.surgeon}>
            {caseItem.surgeon}
          </Typography>
          <Typography sx={procedureSx} title={caseItem.caseType}>
            {caseItem.caseType}
          </Typography>
        </Box>
      </Box>

      <Divider sx={dividerSx} />

      {/* Section 3: Tray count + progress bar — hidden once Arrived, since a
          partial bar next to "Complete" reads as contradictory to OR staff */}
      {!isArrived && (
        <Box sx={progressSectionSx} data-testid="cart-progress-section">
          <Typography sx={progressLabelSx}>
            {t('pages.buildCart.caseList.progressLabel', {
              loaded: traysLoadedOrReserved,
              required: caseItem.traysRequired,
            })}
          </Typography>
          <LinearProgress variant="determinate" value={progressValue} sx={progressBarSx} />
        </Box>
      )}

      {/* Section 4: Cart info + action button (border-top separates from progress) */}
      <Box sx={cartFooterSx}>
        <Box sx={cartInfoSx}>
          <Box sx={cartIconContainerSx}>
            <CartIcon style={cartIconSx} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={cartLabelSx}>
              {t('pages.caseCartAssembly.cartLabel')}
            </Typography>
            <Typography sx={cartNameSx} title={cartLabel}>
              {cartLabel}
            </Typography>
          </Box>
        </Box>

        {isBuilding ? (
          <Button
            variant="contained"
            color="primary"
            size="small"
            sx={actionButtonSx}
            endIcon={<ArrowForward />}
            onClick={() => onSelect(caseItem.id)}
            data-testid="cart-action-button"
          >
            {t(actionI18nKey)}
          </Button>
        ) : (
          <Box sx={cartActionsSx}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              sx={viewButtonSx}
              onClick={() => onSelect(caseItem.id)}
              data-testid="view-items-button"
            >
              {t('pages.caseCartAssembly.viewItemsLabel')}
            </Button>
            {queueStage === CASE_CART_QUEUE_STAGE.READY_FOR_DISPATCH && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                sx={sendToOrButtonSx}
                disabled={isSendingToOr || !canSendToOr}
                onClick={() => onSendToOr?.(caseItem.id)}
                data-testid="send-to-or-button"
              >
                <Box
                  component="span"
                  sx={sendToOrLabelSx}
                  title={sendToOrLabel}
                  data-testid="send-to-or-label"
                >
                  {sendToOrLabel}
                </Box>
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
